import {
  angleDelta,
  computeSnap,
  rectCenter,
  remapBox,
  resizeBounds,
  scaleFactors,
  snapAngle,
  vRotate,
  type BoardNode,
  type NodeId,
  type Rect,
  type ResizeHandle,
  type TextNode,
  type Vec,
} from '@kanvas/board-core';
import { reparentIntoFrames } from '../../actions/actions';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import type { SelectionFrame } from '../../render/handles';
import { snapCandidates } from '../picking';
import { collectGroupMembers } from './select';
import { SNAP_THRESHOLD_PX, type Gesture, type GestureContext } from '../types';

const MIN_EDGE = 8;

interface BoxSnapshot {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  fontSize?: number;
}

function snapshot(node: BoardNode): BoxSnapshot {
  const base: BoxSnapshot = {
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
    rotation: node.rotation,
  };
  if (node.type === 'text') base.fontSize = node.textStyle.fontSize;
  return base;
}

/**
 * Resize gesture.
 *
 * Runs in the selection's own rotated frame: the pointer is un-rotated about
 * the frame centre, the box maths happen axis-aligned, and the result is
 * rotated back. Without that step, dragging a handle on a rotated shape would
 * shear it rather than resize it.
 */
export function createResizeGesture(
  ctx: GestureContext,
  ids: readonly NodeId[],
  handle: ResizeHandle,
  frame: SelectionFrame,
): Gesture {
  const doc = ctx.doc;
  // Notebooks have a fixed cover size (and a fixed inner page); skip them.
  const nodes = collectGroupMembers(doc, ids).filter(
    (n) => n.type !== 'connector' && n.type !== 'notebook' && !n.locked,
  );
  if (nodes.length === 0) return noopGesture();

  const originals = new Map<NodeId, Partial<BoardNode>>();
  const snaps = new Map<NodeId, BoxSnapshot>();
  for (const node of nodes) {
    snaps.set(node.id, snapshot(node));
    originals.set(node.id, snapshot(node) as Partial<BoardNode>);
  }

  const startBounds = frame.bounds;
  const rotation = frame.rotation;
  const startCenter = rectCenter(startBounds);
  const excluded = new Set(nodes.map((n) => n.id));
  const candidates = snapCandidates(doc, useEditor.getState().viewport, excluded);

  let lastFinals = new Map<NodeId, Partial<BoardNode>>();
  let changed = false;

  return {
    move(next) {
      const vp = useEditor.getState().viewport;
      const threshold = SNAP_THRESHOLD_PX / vp.zoom;

      // Un-rotate the pointer into the frame's own axes.
      let local =
        rotation === 0
          ? next.pointer.board
          : vRotate(next.pointer.board, startCenter, -rotation);

      // Snap the dragged pointer to nearby edges and centres. Snapping the
      // pointer (rather than the resulting box) keeps the maths in one place
      // and produces the same guides the move gesture draws.
      const snapAllowed =
        useEditor.getState().snapEnabled && rotation === 0 && !next.mods.primary;
      scratch.guides = [];
      if (snapAllowed) {
        const probe: Rect = { x: local.x, y: local.y, w: 0, h: 0 };
        const snap = computeSnap(probe, candidates, threshold, {
          size: doc.getMeta().gridSize,
          enabled: doc.getMeta().snapToGrid,
        });
        local = { x: local.x + snap.delta.x, y: local.y + snap.delta.y };
        scratch.guides = snap.guides;
      }

      const nextBounds = resizeBounds(startBounds, handle, local, {
        lockAspect: next.mods.shift,
        fromCenter: next.mods.alt,
        min: MIN_EDGE,
      });

      const finals = new Map<NodeId, Partial<BoardNode>>();

      if (nodes.length === 1 && rotation !== 0) {
        // Single rotated node: keep the grabbed corner visually anchored by
        // rotating the new centre back around the original centre.
        const node = nodes[0]!;
        const localCenter = rectCenter(nextBounds);
        const worldCenter = vRotate(localCenter, startCenter, rotation);
        finals.set(node.id, {
          x: worldCenter.x - nextBounds.w / 2,
          y: worldCenter.y - nextBounds.h / 2,
          w: nextBounds.w,
          h: nextBounds.h,
        } as Partial<BoardNode>);
      } else {
        const scale = scaleFactors(startBounds, nextBounds);
        for (const node of nodes) {
          const s = snaps.get(node.id)!;
          const box = remapBox({ x: s.x, y: s.y, w: s.w, h: s.h }, startBounds, nextBounds);
          const patch: Partial<BoardNode> = {
            x: box.x,
            y: box.y,
            w: Math.max(box.w, MIN_EDGE),
            h: Math.max(box.h, MIN_EDGE),
          } as Partial<BoardNode>;

          // Text blocks scale their type when both axes change, and reflow when
          // only the width is dragged. That matches how people expect a text
          // box to behave versus a shape, whose label auto-fits anyway.
          if (node.type === 'text' && s.fontSize !== undefined) {
            const bothAxes = handle.length === 2;
            if (bothAxes) {
              const factor = Math.min(scale.x, scale.y);
              (patch as Partial<TextNode>).textStyle = {
                ...(node as TextNode).textStyle,
                fontSize: Math.max(6, Math.round(s.fontSize * factor)),
              };
            }
          }
          finals.set(node.id, patch);
        }
      }

      changed = true;
      scratch.gestureActive = true;
      lastFinals = finals;
      doc.updateManyEphemeral(finals);
      invalidate();
    },

    up(next) {
      scratch.guides = [];
      scratch.gestureActive = false;
      if (changed && lastFinals.size > 0) {
        doc.commitGesture(originals, lastFinals, () =>
          reparentIntoFrames({ doc, identity: next.identity }, [...lastFinals.keys()]),
        );
      }
      invalidate();
    },

    cancel() {
      scratch.guides = [];
      scratch.gestureActive = false;
      if (changed) doc.updateManyEphemeral(originals);
      invalidate();
    },
  };
}

/**
 * Rotation gesture. A multi-selection rotates as a rigid body: every member
 * gains the same angle and its centre orbits the selection centre.
 */
export function createRotateGesture(
  ctx: GestureContext,
  ids: readonly NodeId[],
  frame: SelectionFrame,
): Gesture {
  const doc = ctx.doc;
  const nodes = collectGroupMembers(doc, ids).filter((n) => n.type !== 'connector' && !n.locked);
  if (nodes.length === 0) return noopGesture();

  const center = rectCenter(frame.bounds);
  const startAngle = Math.atan2(
    ctx.pointer.board.y - center.y,
    ctx.pointer.board.x - center.x,
  );

  const originals = new Map<NodeId, Partial<BoardNode>>();
  const snaps = new Map<NodeId, BoxSnapshot>();
  for (const node of nodes) {
    snaps.set(node.id, snapshot(node));
    originals.set(node.id, {
      x: node.x,
      y: node.y,
      rotation: node.rotation,
    } as Partial<BoardNode>);
  }

  let lastFinals = new Map<NodeId, Partial<BoardNode>>();
  let changed = false;
  let displayAngle = frame.rotation;

  return {
    cursor: 'grabbing',
    move(next) {
      const angle = Math.atan2(
        next.pointer.board.y - center.y,
        next.pointer.board.x - center.x,
      );
      let delta = angleDelta(startAngle, angle);

      if (next.mods.shift) {
        // Snap the resulting absolute angle, not the delta, so 15-degree steps
        // land on the same values regardless of where the drag started.
        const target = snapAngle(frame.rotation + delta, 15);
        delta = target - frame.rotation;
      }
      displayAngle = frame.rotation + delta;

      const finals = new Map<NodeId, Partial<BoardNode>>();
      for (const node of nodes) {
        const s = snaps.get(node.id)!;
        const nodeCenter: Vec = { x: s.x + s.w / 2, y: s.y + s.h / 2 };
        const spun = ids.length === 1 && nodes.length === 1 ? nodeCenter : vRotate(nodeCenter, center, delta);
        finals.set(node.id, {
          x: spun.x - s.w / 2,
          y: spun.y - s.h / 2,
          rotation: s.rotation + delta,
        } as Partial<BoardNode>);
      }

      changed = true;
      scratch.gestureActive = true;
      lastFinals = finals;
      doc.updateManyEphemeral(finals);
      invalidate();
    },

    up() {
      scratch.gestureActive = false;
      if (changed && lastFinals.size > 0) doc.commitGesture(originals, lastFinals);
      invalidate();
    },

    cancel() {
      scratch.gestureActive = false;
      if (changed) doc.updateManyEphemeral(originals);
      invalidate();
    },

    get rotationDegrees() {
      return Math.round((displayAngle * 180) / Math.PI);
    },
  } as Gesture & { rotationDegrees: number };
}

function noopGesture(): Gesture {
  return { move() {}, up() {}, cancel() {} };
}
